import { useEffect, useState } from "react";
import { Send, Clock, Ban, Mail } from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { adminHttp, formatApiError } from "../../lib/adminApi";
import { toast } from "sonner";
import EmailLogModal from "./EmailLogModal";

const TEMPLATE_OPTIONS = [
  { key: "quote_received", label: "Auto-respuesta cotización" },
  { key: "catalog_delivery", label: "Entrega de catálogo" },
  { key: "case_studies_delivery", label: "5 casos de éxito" },
  { key: "preliminary_quote", label: "Cotización preliminar" },
  { key: "nurturing_day_1", label: "Nurturing día 1" },
  { key: "nurturing_day_3", label: "Nurturing día 3" },
  { key: "nurturing_day_7", label: "Nurturing día 7" },
  { key: "post_quote_followup", label: "Follow-up post cotización" },
];

export default function LeadEmailsTab({ leadId, leadEmail, leadName }) {
  const [logs, setLogs] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSend, setShowSend] = useState(false);
  const [template, setTemplate] = useState("quote_received");
  const [overrideEmail, setOverrideEmail] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminHttp.get(`/admin/emails/lead/${leadId}/history`);
      setLogs(data.logs || []);
      setScheduled(data.scheduled || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const manualSend = async () => {
    setSending(true);
    try {
      const body = { template_key: template };
      if (overrideEmail) body.to_email = overrideEmail;
      if (note) body.note = note;
      const { data } = await adminHttp.post(`/admin/emails/lead/${leadId}/send`, body);
      if (data.status === "sent") toast.success("Email enviado");
      else toast.message(`Estado: ${data.status}`);
      setShowSend(false); setNote(""); setOverrideEmail("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const cancel = async (jobId) => {
    if (!window.confirm("¿Cancelar este envío?")) return;
    try {
      await adminHttp.post(`/admin/emails/scheduled/${jobId}/cancel`);
      toast.success("Cancelado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="lead-emails-tab">
      {/* Action bar */}
      <div className="bg-white border border-[#DDE2D5] p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">Emails asociados</p>
            <p className="mt-1 text-[#1F2937] text-sm">Historial completo de comunicaciones del lead.</p>
          </div>
          <button onClick={() => setShowSend((s) => !s)} className="btn-accent py-3 px-5 text-xs" data-testid="lead-manual-send-toggle">
            <Send className="w-4 h-4 mr-1" /> Enviar email manual
          </button>
        </div>
        {showSend && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-[#DDE2D5] pt-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">Plantilla</label>
              <select value={template} onChange={(e) => setTemplate(e.target.value)}
                      className="mt-1 w-full bg-white border border-[#DDE2D5] px-3 py-3 text-[#1F2937] text-sm focus:outline-none focus:border-accent"
                      data-testid="manual-template">
                {TEMPLATE_OPTIONS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">Destinatario override</label>
              <input value={overrideEmail} onChange={(e) => setOverrideEmail(e.target.value)}
                     placeholder={leadEmail || "Lead sin email; especifica"}
                     className="mt-1 w-full bg-white border border-[#DDE2D5] px-3 py-3 text-[#1F2937] text-sm focus:outline-none focus:border-accent"
                     data-testid="manual-override-email" />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">Nota interna (opcional)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                     className="mt-1 w-full bg-white border border-[#DDE2D5] px-3 py-3 text-[#1F2937] text-sm focus:outline-none focus:border-accent" />
            </div>
            <div className="md:col-span-2">
              <button onClick={manualSend} disabled={sending} className="btn-accent py-3 px-6 text-xs" data-testid="manual-send-confirm">
                {sending ? "Enviando…" : "Enviar ahora"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Scheduled */}
      {scheduled.length > 0 && (
        <div className="bg-white border border-[#DDE2D5]">
          <div className="px-6 py-4 border-b border-[#DDE2D5]">
            <h3 className="font-display font-black text-lg">Programados</h3>
          </div>
          <div className="divide-y divide-white/5">
            {scheduled.map((j) => (
              <div key={j.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3 text-accent" />
                    <span className="font-mono text-[11px] text-accent uppercase tracking-wider">{j.template_key}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[#6B7280]">· {j.status}</span>
                  </div>
                  <p className="font-mono text-[11px] text-[#374151] mt-1">
                    {format(parseISO(j.scheduled_for), "d MMM yyyy · HH:mm", { locale: es })}
                  </p>
                </div>
                {j.status === "pending" && (
                  <button onClick={() => cancel(j.id)} className="p-2 text-[#4B5563] hover:text-red-400" title="Cancelar">
                    <Ban className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logs timeline */}
      <div className="bg-white border border-[#DDE2D5]">
        <div className="px-6 py-4 border-b border-[#DDE2D5]">
          <h3 className="font-display font-black text-lg flex items-center gap-2"><Mail className="w-4 h-4 text-accent" /> Historial de envíos</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-[#6B7280] font-mono text-xs">Cargando…</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-[#6B7280] font-mono text-xs">Sin emails enviados aún.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {logs.map((l) => (
              <button key={l.id} onClick={() => setOpenId(l.id)}
                      className="w-full text-left px-6 py-4 hover:bg-[#F2F4ED]/50 transition">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-accent uppercase tracking-wider">{l.template_key}</span>
                      <StatusBadge status={l.status} />
                    </div>
                    <p className="text-[#374151] text-sm mt-1 truncate">{l.subject}</p>
                    <p className="font-mono text-[10px] text-[#6B7280] uppercase tracking-wider mt-1">
                      {format(parseISO(l.created_at), "d MMM · HH:mm", { locale: es })} · {(l.to || []).join(", ")}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {openId && <EmailLogModal logId={openId} onClose={() => setOpenId(null)} onResend={() => setOpenId(null)} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: { bg: "#1e4d29", text: "#7fff9f" }, failed: { bg: "#4d1e1e", text: "#ff7f7f" },
    queued: { bg: "#1a1a2e", text: "#8a8aff" }, skipped: { bg: "#2e2e2e", text: "#aaa" },
  };
  const c = map[status] || { bg: "#2e2e2e", text: "#aaa" };
  return <span style={{ background: c.bg, color: c.text }} className="px-2 py-0.5 text-[9px] font-mono uppercase tracking-wider">{status}</span>;
}
